/**
 * Convert a bank CSV export into Beancount double-entry transactions.
 *
 * Pure and dependency-free — no DOM, no Node, no CSV library (the repo
 * deliberately has none). Everything the `/tools/csv-to-beancount` page does
 * goes through here so the behavior is unit-testable without a browser.
 *
 * The pipeline is: {@link parseCsv} → {@link detectMapping} (+ the user's
 * corrections) → {@link emitBeancount}. Rows that can't be understood are
 * *reported*, never thrown, so one bad line can't cost a reader the other 500.
 */

import {
  DEFAULT_CURRENCY,
  isValidAccount,
  isValidCommodity,
  quote,
} from "@/lib/beancount-syntax";
import {
  findMatchingDescriptionRule,
  validateCsvDescriptionRules,
  type CsvDescriptionRule,
  type ValidatedCsvDescriptionRule,
} from "@/lib/csv-description-rules";
import {
  csvSplitIssueReason,
  indexCsvRowSplits,
  resolveCsvRowSplit,
  type CsvResolvedSplit,
  type CsvRowSplit,
  type CsvSplitDiagnostic,
} from "@/lib/csv-split-allocations";
import {
  OPENING_BALANCES_ACCOUNT,
  planMatchedStatementEmission,
  reconcileStatementFromSelection,
  selectRowsForReconciliation,
  type StatementReconciliationResult,
} from "@/lib/csv-statement-reconciliation";
import { toCsvExcludeSet } from "@/lib/csv-exclude-set";
import { sumFixedTexts } from "@/lib/fixed-point-text";
import {
  detectDateOrder,
  normalizeAmount,
  normalizeDate,
  parseAmount,
  type DateOrder,
  type ParsedAmount,
} from "@/lib/import-normalize";

export {
  DEFAULT_CURRENCY,
  isValidAccount,
  isValidCommodity,
} from "@/lib/beancount-syntax";

/**
 * Which artifact the emitter builds. `complete` is a standalone ledger a new
 * user can validate as-is; `append` is transactions to paste into a ledger
 * that already declares its accounts.
 */
export type CsvOutputMode = "complete" | "append";

/** A parsed CSV: an optional header row plus the data rows. */
export interface ParsedCsv {
  /** Column titles, or `null` when the file has no header row. */
  headers: string[] | null;
  rows: string[][];
  /** The delimiter that was detected. */
  delimiter: string;
  /** Widest row, so callers don't re-derive it from ragged rows. */
  width: number;
}

/** Which column holds what. `null` means "not present / not mapped". */
export interface ColumnMapping {
  date: number | null;
  description: number | null;
  /** A single signed amount column. */
  amount: number | null;
  /** Split columns: money out. Used when `amount` is null. */
  debit: number | null;
  /** Split columns: money in. Used when `amount` is null. */
  credit: number | null;
  /** Per-row currency code, overriding the default. */
  currency: number | null;
  /**
   * Source category column (e.g. a bank export's `Category`). Optional so
   * mappings built before this field existed keep working: `undefined`
   * reads as unmapped, exactly like `null`.
   */
  category?: number | null;
}

export interface ConvertOptions {
  /** The account the statement belongs to. */
  sourceAccount?: string;
  /** Where the other leg goes until it's categorized. */
  counterAccount?: string;
  /**
   * Explicit category-value → destination-account mapping. The key is the
   * trimmed cell text of the mapped category column; the value is the
   * account that row's other leg posts to. A row whose category is empty
   * or has no entry falls back to `counterAccount` — it is never dropped.
   * Every value must be a valid account (an invalid one blocks conversion
   * with `invalid account: <value>`); a category never synthesizes an
   * account name, and the amount sign never selects one.
   */
  categoryAccounts?: Readonly<Record<string, string>>;
  /**
   * Ordered literal description → account rules (see
   * `@/lib/csv-description-rules`). Precedence per row is: an explicit
   * category-account assignment wins, then the first enabled matching rule,
   * then `counterAccount`. Rows whose category is empty or unmapped may
   * still match a rule. `undefined` (or an empty array) reads as "no rules"
   * and leaves output byte-identical to current behavior. Invalid entries
   * block conversion with line-0 `invalid …` errors, exactly like invalid
   * category accounts — they never silently become valid.
   */
  descriptionRules?: readonly CsvDescriptionRule[];
  /** Fallback currency when no currency column is mapped. */
  currency?: string;
  /**
   * Which artifact to build. Defaults to `append` so existing callers keep
   * byte-identical transaction output; the widget selects `complete` for the
   * first-ledger path.
   */
  outputMode?: CsvOutputMode;
  /** Flip every sign — for exports where outflows are positive. */
  invertAmounts?: boolean;
  /**
   * Column stating the direction, for exports whose amount column is an
   * unsigned magnitude (Mint writes `84.23` + `debit`). Applies only to a
   * single mapped `amount` column; the split debit/credit path already knows
   * its own signs.
   */
  signColumn?: number;
  /** Values in {@link signColumn} meaning money out, compared case-insensitively. */
  debitValues?: readonly string[];
  dateOrder?: DateOrder;
  /** `*` (cleared) or `!` (needs review). */
  flag?: string;
  /**
   * 0-based data-row indexes to omit from the artifact. Errors on those
   * rows are also suppressed — the reader already decided not to import
   * them. Line numbers on remaining diagnostics stay tied to the original
   * file; filtering the row array first would renumber them.
   */
  excludeRowIndexes?: ReadonlySet<number> | readonly number[];
  /**
   * Optional explicit statement balances. When omitted or `enabled: false`,
   * output is unchanged. When enabled, balances are ordinary signed decimal
   * text (never raw Beancount syntax). A **matched** selection may add
   * directives: complete mode establishes a nonzero opening once via
   * {@link OPENING_BALANCES_ACCOUNT}; append mode may assert closing only and
   * **never** inserts another opening transaction — it depends on the
   * existing ledger already carrying that opening. Mismatch/invalid paths
   * keep ordinary conversion with no statement directives and no plug.
   */
  statement?: CsvStatementBalanceOptions;
  /**
   * Explicit per-row split edits (see `@/lib/csv-split-allocations`). Each
   * names a data-row index plus the {@link preparedRowSplitKey} of the row it
   * was made for. A valid split replaces that row's single other leg with
   * one signed contra posting per allocation; the source posting, overlap
   * identity and statement contribution are unchanged. An invalid, stale or
   * incomplete split on a **selected** row is reported (in `errors` and
   * `splitDiagnostics`) and the row is not emitted — it never silently falls
   * back to the unsplit posting. Splits on excluded rows are ignored.
   * `undefined` or an empty list leaves output byte-identical.
   */
  rowSplits?: readonly CsvRowSplit[];
}

/**
 * Reader-entered statement window and signed balances for optional
 * reconciliation. Fields are plain dates/amounts — not Beancount source.
 */
export interface CsvStatementBalanceOptions {
  enabled: boolean;
  startDateText: string;
  endDateText: string;
  /** Signed opening balance text (empty → incomplete when enabled). */
  openingText: string;
  /** Signed closing balance text (empty → incomplete when enabled). */
  closingText: string;
  /**
   * Statement commodity. Defaults to {@link ConvertOptions.currency} / USD.
   * Must match every selected row for a match.
   */
  currency?: string;
}

/** A row that could not be converted, identified by its line in the file. */
export interface RowError {
  /** 1-based line number in the original file, header included. */
  line: number;
  reason: string;
}

/**
 * One data row after the same date/amount/sign/currency rules {@link emitBeancount}
 * applies. Shared with overlap review so a "match" means the converter would
 * emit the same values — never a second, softer interpretation.
 */
export interface PreparedCsvRow {
  /** 0-based index into the data `rows` array. */
  rowIndex: number;
  /** 1-based line in the original file, header included. */
  line: number;
  date: string;
  /** Signed amount after invert / sign-column rules. */
  amount: number;
  decimals: number;
  currency: string;
  description: string;
  /** Other-leg account after category lookup / rule match / counter fallback. */
  counterAccount: string;
  /**
   * How `counterAccount` was chosen — the bounded reason the preview and
   * the emitter share, since both read it off the same prepared row.
   * `category` is an explicit category-account assignment, `rule` the first
   * enabled matching description rule, `fallback` the counter account.
   */
  accountSource: CsvCounterAccountSource;
  /**
   * Stable id of the first enabled matching description rule. Present only
   * when `accountSource` is `"rule"`; absent otherwise.
   */
  matchedRuleId?: string;
  /**
   * The validated split for this row, when {@link ConvertOptions.rowSplits}
   * carries a valid one. Replaces `counterAccount` as the other side of the
   * transaction; absent on every unsplit row.
   */
  split?: CsvResolvedSplit;
}

/** How a prepared row's other-leg account was chosen. */
export type CsvCounterAccountSource = "category" | "rule" | "fallback";

export type PrepareCsvRowResult =
  | { ok: true; row: PreparedCsvRow }
  | { ok: false; rowIndex: number; line: number; reason: string };

export interface ConvertResult {
  text: string;
  errors: RowError[];
  /** How many transactions were emitted. */
  converted: number;
  /**
   * How many data rows the reader explicitly excluded. Distinct from
   * `errors` — an exclusion is intentional, not a conversion failure.
   */
  excluded: number;
  /**
   * The date order actually used. `"ambiguous"` means the column held
   * two-part dates that couldn't be told apart and `mdy` was assumed — the
   * one case where a UI should offer the reader a choice.
   */
  dateOrder: DateOrder | "ambiguous";
  /** The mode that produced `text`, after defaulting. */
  outputMode: CsvOutputMode;
  /** Every account the emitted postings touch, sorted. Empty when nothing converted. */
  accounts: string[];
  /** Every commodity the emitted postings carry, sorted. Empty when nothing converted. */
  currencies: string[];
  /** Earliest emitted transaction date, for dating `open` directives. Null when nothing converted. */
  openDate: string | null;
  /**
   * Present when {@link ConvertOptions.statement} was passed (including
   * `enabled: false`). Absent when the option was omitted, so legacy callers
   * keep a byte-identical result shape aside from this optional field.
   */
  statement?: StatementReconciliationResult;
  /**
   * True only when statement directives were woven into `text` (matched +
   * emittable). Mismatch/invalid never set this — ordinary conversion only.
   */
  statementReconciled: boolean;
  /**
   * Split problems on selected rows (plus splits naming no row at all),
   * ordered by row. Present only when {@link ConvertOptions.rowSplits} was
   * passed; an empty array then means every selected split is valid.
   */
  splitDiagnostics?: CsvSplitDiagnostic[];
}

const DELIMITERS = [",", ";", "\t", "|"] as const;

/**
 * Pick the delimiter by counting candidates outside quotes on the first line.
 * European exports are frequently semicolon-separated, and guessing wrong
 * turns every row into one giant field.
 */
function detectDelimiter(text: string): string {
  const firstLine = text.slice(0, text.indexOf("\n") + 1 || undefined);
  let best = ",";
  let bestCount = 0;
  for (const d of DELIMITERS) {
    let count = 0;
    let inQuotes = false;
    for (let i = 0; i < firstLine.length; i++) {
      const ch = firstLine[i];
      if (ch === '"') inQuotes = !inQuotes;
      else if (ch === d && !inQuotes) count++;
    }
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  }
  return best;
}

/** RFC 4180 reader: quoted fields, `""` escapes, embedded newlines, CRLF. */
function readRows(text: string, delimiter: string): string[][] {
  const rows: string[][] = [];
  let row: string[] = [];
  let field = "";
  let inQuotes = false;
  let started = false;

  for (let i = 0; i < text.length; i++) {
    const ch = text[i];
    if (inQuotes) {
      if (ch === '"') {
        if (text[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          inQuotes = false;
        }
      } else {
        field += ch;
      }
      continue;
    }
    if (ch === '"') {
      inQuotes = true;
      started = true;
    } else if (ch === delimiter) {
      row.push(field);
      field = "";
      started = true;
    } else if (ch === "\n") {
      row.push(field);
      rows.push(row);
      row = [];
      field = "";
      started = false;
    } else if (ch !== "\r") {
      field += ch;
      started = true;
    }
  }
  if (started || field !== "" || row.length > 0) {
    row.push(field);
    rows.push(row);
  }

  // Drop blank lines (trailing newline always produces one).
  return rows.filter((r) => r.some((cell) => cell.trim() !== ""));
}

/** A value that reads as a number in any of the supported formats. */
function looksNumeric(value: string): boolean {
  return normalizeAmount(value) !== null;
}

/** A value that reads as a date in any of the supported formats. */
function looksLikeDate(value: string): boolean {
  return "date" in normalizeDate(value);
}

/**
 * Parse CSV text, detecting the delimiter and whether the first row is a
 * header. A first row is treated as a header when it carries no date and no
 * number but a later row does — the shape banks actually export.
 */
export function parseCsv(text: string): ParsedCsv {
  const delimiter = detectDelimiter(text);
  const all = readRows(text, delimiter);
  const width = all.reduce((max, row) => Math.max(max, row.length), 0);
  if (all.length === 0) return { headers: null, rows: [], delimiter, width };

  const [first, ...rest] = all;
  const firstIsData = first.some((c) => looksLikeDate(c) || looksNumeric(c));

  if (!firstIsData && rest.length > 0) {
    return {
      headers: first.map((h) => h.trim()),
      rows: rest,
      delimiter,
      width,
    };
  }
  return { headers: null, rows: all, delimiter, width };
}

const HEADER_PATTERNS: Array<[keyof ColumnMapping, RegExp]> = [
  ["date", /\b(date|posted|buchungstag|datum|fecha|data)\b/i],
  ["debit", /\b(debit|withdrawal|paid out|outflow|soll|belastung)\b/i],
  ["credit", /\b(credit|deposit|paid in|inflow|haben|gutschrift)\b/i],
  ["amount", /\b(amount|betrag|value|montant|importe|valore)\b/i],
  [
    "description",
    /\b(descr\w*|payee|memo|narration|details?|reference|name|verwendungszweck|concepto)\b/i,
  ],
  ["currency", /\b(currency|ccy|w(?:a|ä)hrung|devise|moneda)\b/i],
  ["category", /\b(categor\w*|kategor\w*|cat[eé]gories?)\b/i],
];

/** What one column looks like, measured in a single pass over the rows. */
interface ColumnStats {
  /** Fraction of non-empty cells that read as a number. */
  numeric: number;
  /** Fraction of non-empty cells that read as a date. */
  date: number;
  /** Mean cell length — how wordy the column is. */
  avgLength: number;
}

/**
 * How many rows the column heuristics look at. Detection only has to decide
 * which column holds dates — a decision the reader can override with a select —
 * so reading the whole file is wasted work: on a 5,000-row export, sampling
 * takes detection from ~41 ms to ~1 ms, and it runs on every keystroke.
 */
const DETECTION_SAMPLE_ROWS = 200;

/**
 * Measure every column once over a sample. Each cell is otherwise parsed up to
 * three times (date scan, amount scan, description scan).
 */
function columnStats(rows: string[][], width: number): ColumnStats[] {
  const sample =
    rows.length > DETECTION_SAMPLE_ROWS
      ? rows.slice(0, DETECTION_SAMPLE_ROWS)
      : rows;
  return columnStatsOver(sample, width);
}

function columnStatsOver(rows: string[][], width: number): ColumnStats[] {
  const stats: ColumnStats[] = Array.from({ length: width }, () => ({
    numeric: 0,
    date: 0,
    avgLength: 0,
  }));
  const nonEmpty = new Array<number>(width).fill(0);

  for (const row of rows) {
    for (let i = 0; i < width; i++) {
      const value = (row[i] ?? "").trim();
      stats[i].avgLength += value.length;
      if (value === "") continue;
      nonEmpty[i] += 1;
      // Counted independently: a value can satisfy both tests.
      if (looksNumeric(value)) stats[i].numeric += 1;
      if (looksLikeDate(value)) stats[i].date += 1;
    }
  }

  const rowCount = Math.max(rows.length, 1);
  for (let i = 0; i < width; i++) {
    const seen = Math.max(nonEmpty[i], 1);
    stats[i] = {
      numeric: stats[i].numeric / seen,
      date: stats[i].date / seen,
      avgLength: stats[i].avgLength / rowCount,
    };
  }
  return stats;
}

/** Highest-scoring free column above `floor`, or `null` if none qualifies. */
function pickColumn(
  width: number,
  isTaken: (index: number) => boolean,
  score: (index: number) => number,
  floor: number,
): number | null {
  let best: number | null = null;
  let bestScore = floor;
  for (let i = 0; i < width; i++) {
    if (isTaken(i)) continue;
    const value = score(i);
    if (value > bestScore) {
      best = i;
      bestScore = value;
    }
  }
  return best;
}

/**
 * Guess which column is which — from header names first, then from the shape
 * of the values when a header is missing or unrecognized. The UI shows the
 * result as editable selects, so a wrong guess is a correction, not a failure.
 */
export function detectMapping(
  headers: string[] | null,
  rows: string[][],
): ColumnMapping {
  const mapping: ColumnMapping = {
    date: null,
    description: null,
    amount: null,
    debit: null,
    credit: null,
    currency: null,
    category: null,
  };

  if (headers) {
    for (const [field, pattern] of HEADER_PATTERNS) {
      if (mapping[field] !== null) continue;
      const index = headers.findIndex(
        (h, i) => pattern.test(h) && !Object.values(mapping).includes(i),
      );
      if (index !== -1) mapping[field] = index;
    }
  }

  const width = rows.reduce(
    (max, row) => Math.max(max, row.length),
    headers?.length ?? 0,
  );
  const stats = columnStats(rows, width);
  const isTaken = (index: number) => Object.values(mapping).includes(index);

  // Confidence floor: most of a column's values must fit before it's claimed.
  const CONFIDENT = 0.6;

  mapping.date ??= pickColumn(width, isTaken, (i) => stats[i].date, CONFIDENT);

  if (mapping.amount === null && mapping.debit === null) {
    mapping.amount = pickColumn(
      width,
      isTaken,
      (i) => stats[i].numeric,
      CONFIDENT,
    );
  }

  // The wordiest column that isn't mostly numbers and isn't already claimed.
  mapping.description ??= pickColumn(
    width,
    isTaken,
    (i) => (stats[i].numeric < 0.5 ? stats[i].avgLength : 0),
    0,
  );

  return mapping;
}

export const DEFAULT_SOURCE_ACCOUNT = "Assets:Checking";
export const DEFAULT_COUNTER_ACCOUNT = "Expenses:Uncategorized";

/**
 * Re-sign a magnitude in place: `out` means money leaving the source account.
 *
 * Both callers own a `ParsedAmount` that `parseAmount` allocated one line
 * earlier and nothing else holds, so this mutates rather than cloning — it runs
 * once per row, and a 5,000-row paste re-renders on a deferred transition.
 */
function directed(parsed: ParsedAmount, out: boolean): ParsedAmount {
  parsed.value = out ? -Math.abs(parsed.value) : Math.abs(parsed.value);
  return parsed;
}

/**
 * Exact amount text the emitter writes for a prepared row. Using the same
 * `toFixed(decimals)` string for overlap keys avoids float-rounding fingerprints.
 */
export function preparedAmountText(row: PreparedCsvRow): string {
  return row.amount.toFixed(row.decimals);
}

/**
 * Net contribution of one prepared row to the source account: the exact sum
 * of every posting {@link projectCsvRowPostings} emits to that account.
 *
 * Derived from the emitted postings, never from the CSV amount, so the
 * statement movement is the ledger's movement whatever the row's shape:
 * an ordinary row contributes its source-leg text byte for byte; when the
 * counter is the source, `+amount` and `-amount` land on the same account
 * and the net is zero at the row's decimals; a split row's contra postings
 * never touch the source (the resolver refuses it), so it contributes its
 * full source amount. Callers that sum statement movement must use this
 * rather than {@link preparedAmountText} alone.
 */
export function preparedSourceContributionText(
  row: PreparedCsvRow,
  sourceAccount: string,
): string {
  const sourceTexts = projectCsvRowPostings(row, sourceAccount)
    .filter((posting) => posting.account === sourceAccount)
    .map((posting) => posting.amountText);
  if (sourceTexts.length === 1) return sourceTexts[0];
  // Emitted texts are always plain fixed-point; the fallback is unreachable
  // but keeps the zero contribution at the row's precision.
  return sumFixedTexts(sourceTexts) ?? (0).toFixed(row.decimals);
}

/** One emitted posting line: account, signed fixed-point text, commodity. */
interface CsvPosting {
  account: string;
  amountText: string;
  currency: string;
}

/**
 * The postings {@link emitBeancount} writes for one prepared row, in order:
 * the source posting, then either the single unsplit other leg or one contra
 * posting per validated split allocation. This is the one projection the
 * emitter (and any preview) reads, so a split cannot render one way and
 * download another.
 */
export function projectCsvRowPostings(
  row: PreparedCsvRow,
  sourceAccount: string,
): CsvPosting[] {
  const { currency } = row;
  const postings: CsvPosting[] = [
    { account: sourceAccount, amountText: preparedAmountText(row), currency },
  ];
  if (row.split === undefined) {
    postings.push({
      account: row.counterAccount,
      amountText: (-row.amount).toFixed(row.decimals),
      currency,
    });
  } else {
    for (const allocation of row.split.allocations) {
      postings.push({ ...allocation, currency });
    }
  }
  return postings;
}

/**
 * Identity a {@link CsvRowSplit} is keyed to: the row index plus the same
 * date/amount/currency/description fingerprint overlap review uses. It
 * deliberately ignores the chosen counter account, so re-mapping a category
 * keeps the split, while a changed source file invalidates it.
 */
export function preparedRowSplitKey(row: PreparedCsvRow): string {
  return `${row.rowIndex}\u0000${preparedRowMatchKey(row)}`;
}

/**
 * Stable equality key for occurrence matching: date + signed amount text +
 * currency + description. Description is exact (trimmed); no fuzzy matching.
 */
export function preparedRowMatchKey(row: PreparedCsvRow): string {
  return [
    row.date,
    preparedAmountText(row),
    row.currency,
    row.description,
  ].join("\u0000");
}

interface ResolvedConvertContext {
  source: string;
  counter: string;
  fallbackCurrency: string;
  outputMode: CsvOutputMode;
  flag: string;
  sign: number;
  signColumn: number | undefined;
  debitValues: Set<string>;
  order: DateOrder;
  resolvedOrder: DateOrder | "ambiguous";
  categoryIndex: number | null;
  categoryAccounts: Map<string, string>;
  descriptionRules: ValidatedCsvDescriptionRule[];
  blockingErrors: RowError[];
}

function resolveConvertContext(
  rows: string[][],
  mapping: ColumnMapping,
  options: ConvertOptions,
): ResolvedConvertContext {
  const source = (options.sourceAccount || DEFAULT_SOURCE_ACCOUNT).trim();
  const counter = (options.counterAccount || DEFAULT_COUNTER_ACCOUNT).trim();
  const fallbackCurrency = (options.currency || DEFAULT_CURRENCY).trim();
  const outputMode: CsvOutputMode = options.outputMode ?? "append";
  const flag = options.flag || "*";
  const sign = options.invertAmounts ? -1 : 1;
  const signColumn = options.signColumn;
  const debitValues = new Set(
    (options.debitValues ?? []).map((value) => value.trim().toLowerCase()),
  );
  const detected =
    options.dateOrder !== undefined || mapping.date === null
      ? null
      : detectDateOrder(rows.map((r) => (r[mapping.date!] ?? "").trim()));
  const order =
    options.dateOrder ??
    (detected === "ambiguous" || detected === null ? "mdy" : detected);
  const resolvedOrder: DateOrder | "ambiguous" =
    options.dateOrder ?? (detected === "ambiguous" ? "ambiguous" : order);

  const blockingErrors: RowError[] = [];
  if (mapping.date === null) {
    blockingErrors.push({ line: 0, reason: "no date column mapped" });
  }
  if (
    mapping.amount === null &&
    mapping.debit === null &&
    mapping.credit === null
  ) {
    blockingErrors.push({ line: 0, reason: "no amount column mapped" });
  }
  if (!isValidAccount(source)) {
    blockingErrors.push({ line: 0, reason: `invalid account: ${source}` });
  }
  if (!isValidAccount(counter)) {
    blockingErrors.push({ line: 0, reason: `invalid account: ${counter}` });
  }
  const categoryIndex = mapping.category ?? null;
  const categoryAccounts = new Map<string, string>();
  for (const [rawKey, rawValue] of Object.entries(
    options.categoryAccounts ?? {},
  )) {
    const key = rawKey.trim();
    const value = rawValue.trim();
    if (key === "") continue;
    if (!isValidAccount(value)) {
      blockingErrors.push({ line: 0, reason: `invalid account: ${value}` });
    } else {
      categoryAccounts.set(key, value);
    }
  }
  if (!isValidCommodity(fallbackCurrency)) {
    blockingErrors.push({
      line: 0,
      reason: `invalid currency: ${fallbackCurrency}`,
    });
  }
  const validatedRules = validateCsvDescriptionRules(options.descriptionRules);
  for (const reason of validatedRules.errors) {
    blockingErrors.push({ line: 0, reason });
  }

  return {
    source,
    counter,
    fallbackCurrency,
    outputMode,
    flag,
    sign,
    signColumn,
    debitValues,
    order,
    resolvedOrder,
    categoryIndex,
    categoryAccounts,
    descriptionRules: validatedRules.rules,
    blockingErrors,
  };
}

/**
 * Apply the converter's date/amount/sign/currency rules to one data row.
 * Pure: callers decide whether the row is emitted, reviewed or reported.
 */
function prepareCsvRow(
  row: string[],
  rowIndex: number,
  mapping: ColumnMapping,
  ctx: {
    hasHeader: boolean;
    order: DateOrder;
    sign: number;
    signColumn?: number;
    debitValues: ReadonlySet<string>;
    fallbackCurrency: string;
    counter: string;
    categoryIndex: number | null;
    categoryAccounts: ReadonlyMap<string, string>;
    descriptionRules: readonly ValidatedCsvDescriptionRule[];
  },
): PrepareCsvRowResult {
  const line = rowIndex + (ctx.hasHeader ? 2 : 1);
  const cell = (i: number | null) => (i === null ? "" : (row[i] ?? "").trim());

  const parsedDate = normalizeDate(cell(mapping.date), ctx.order);
  if ("error" in parsedDate) {
    return { ok: false, rowIndex, line, reason: parsedDate.error };
  }

  let parsed: ParsedAmount | null;
  if (mapping.amount !== null) {
    parsed = parseAmount(cell(mapping.amount));
    if (parsed !== null && ctx.signColumn !== undefined) {
      directed(
        parsed,
        ctx.debitValues.has(cell(ctx.signColumn).toLowerCase()),
      );
    }
  } else {
    const debit = parseAmount(cell(mapping.debit));
    const credit = parseAmount(cell(mapping.credit));
    if (debit && debit.value !== 0) {
      parsed = directed(debit, true);
    } else if (credit && credit.value !== 0) {
      parsed = directed(credit, false);
    } else {
      parsed = null;
    }
  }
  if (parsed === null) {
    return { ok: false, rowIndex, line, reason: "unrecognized amount" };
  }

  const amount = parsed.value * ctx.sign;
  const currency = cell(mapping.currency) || ctx.fallbackCurrency;
  if (!isValidCommodity(currency)) {
    return {
      ok: false,
      rowIndex,
      line,
      reason: `invalid currency: ${currency}`,
    };
  }

  const description = cell(mapping.description);
  // Explicit category assignment wins; an empty or unmapped category may
  // still match a description rule; otherwise the counter account applies.
  const categoryValue = cell(ctx.categoryIndex);
  const assigned =
    categoryValue !== ""
      ? ctx.categoryAccounts.get(categoryValue)
      : undefined;
  let counterAccount = ctx.counter;
  let accountSource: CsvCounterAccountSource = "fallback";
  let matchedRuleId: string | undefined;
  if (assigned !== undefined) {
    counterAccount = assigned;
    accountSource = "category";
  } else {
    const matched = findMatchingDescriptionRule(
      ctx.descriptionRules,
      description,
    );
    if (matched !== null) {
      counterAccount = matched.account;
      accountSource = "rule";
      matchedRuleId = matched.id;
    }
  }

  return {
    ok: true,
    row: {
      rowIndex,
      line,
      date: parsedDate.date,
      amount,
      decimals: parsed.decimals,
      currency,
      description,
      counterAccount,
      accountSource,
      ...(matchedRuleId !== undefined ? { matchedRuleId } : {}),
    },
  };
}

/**
 * Prepare every data row with the same context {@link emitBeancount} uses.
 * Blocking configuration errors yield an empty list and the errors array;
 * per-row failures stay in the results as `{ ok: false }`.
 */
function prepareCsvRowsWithContext(
  rows: string[][],
  mapping: ColumnMapping,
  options: ConvertOptions = {},
  hasHeader = true,
): {
  results: PrepareCsvRowResult[];
  blockingErrors: RowError[];
  context: ResolvedConvertContext;
  splitDiagnostics: CsvSplitDiagnostic[];
} {
  const context = resolveConvertContext(rows, mapping, options);
  if (context.blockingErrors.length > 0) {
    return {
      results: [],
      blockingErrors: context.blockingErrors,
      context,
      splitDiagnostics: [],
    };
  }

  const results = rows.map((row, rowIndex) =>
    prepareCsvRow(row, rowIndex, mapping, {
      hasHeader,
      order: context.order,
      sign: context.sign,
      signColumn: context.signColumn,
      debitValues: context.debitValues,
      fallbackCurrency: context.fallbackCurrency,
      counter: context.counter,
      categoryIndex: context.categoryIndex,
      categoryAccounts: context.categoryAccounts,
      descriptionRules: context.descriptionRules,
    }),
  );
  const splitDiagnostics = applyRowSplits(
    results,
    options.rowSplits,
    context.source,
  );
  return { results, blockingErrors: [], context, splitDiagnostics };
}

/**
 * Attach each valid split to its prepared row (in place — the results were
 * allocated by the caller one line earlier) and collect a diagnostic for
 * every split that cannot apply. Diagnostics cover every row; selection
 * (exclusion) is the emitter's decision, not this function's.
 */
function applyRowSplits(
  results: PrepareCsvRowResult[],
  rowSplits: readonly CsvRowSplit[] | undefined,
  sourceAccount: string,
): CsvSplitDiagnostic[] {
  const diagnostics: CsvSplitDiagnostic[] = [];
  for (const [rowIndex, split] of indexCsvRowSplits(rowSplits)) {
    const prepared = results[rowIndex];
    if (prepared === undefined) {
      diagnostics.push({ rowIndex, issues: [{ code: "unknown_row" }] });
      continue;
    }
    if (!prepared.ok) {
      diagnostics.push({
        rowIndex,
        line: prepared.line,
        issues: [{ code: "row_unavailable" }],
      });
      continue;
    }
    const row = prepared.row;
    const resolution = resolveCsvRowSplit(
      split,
      {
        sourceKey: preparedRowSplitKey(row),
        amountText: preparedAmountText(row),
      },
      sourceAccount,
    );
    if (resolution.ok) {
      row.split = resolution.split;
    } else {
      diagnostics.push({ rowIndex, line: row.line, issues: resolution.issues });
    }
  }
  return diagnostics.sort((a, b) => a.rowIndex - b.rowIndex);
}

export function prepareCsvRows(
  rows: string[][],
  mapping: ColumnMapping,
  options: ConvertOptions = {},
  hasHeader = true,
): {
  results: PrepareCsvRowResult[];
  blockingErrors: RowError[];
  /** Split problems on any row (exclusion not applied); see {@link ConvertOptions.rowSplits}. */
  splitDiagnostics: CsvSplitDiagnostic[];
} {
  const { results, blockingErrors, splitDiagnostics } =
    prepareCsvRowsWithContext(rows, mapping, options, hasHeader);
  return { results, blockingErrors, splitDiagnostics };
}

/**
 * Render mapped rows as balanced Beancount transactions.
 *
 * Every transaction's postings sum to zero: two by default, or the source
 * posting plus one contra posting per allocation when a valid
 * {@link ConvertOptions.rowSplits} entry applies (see
 * {@link projectCsvRowPostings}). Unconvertible rows
 * are collected in `errors` with their line number and skipped — a single
 * malformed row never costs the rest. In `complete` mode the result is a
 * standalone ledger: `open` directives for every touched account, dated at
 * the earliest emitted transaction (or the opening-balance date when a
 * matched statement emits one), ahead of the same transaction body that
 * `append` mode returns alone.
 *
 * When {@link ConvertOptions.statement} is enabled and the selection
 * **matches**, complete mode may prepend an explicit opening transaction
 * (nonzero openings only) and both modes append a closing `balance` on the
 * calendar day after the statement end. Append never inserts an opening —
 * that depends on the existing ledger. Mismatch/invalid never add statement
 * directives or a balancing plug.
 *
 * When a category column is mapped, each row's other leg posts to the
 * account its trimmed category value names in `categoryAccounts`; a row
 * whose category is empty or unmapped may still match a description rule
 * (see `descriptionRules`) and otherwise falls back to `counterAccount`,
 * still converting. The mapped account is used as-is for either amount sign —
 * the sign never selects an account type — and a category never invents one.
 *
 * {@link ConvertOptions.excludeRowIndexes} omits selected data rows from the
 * artifact without renumbering diagnostics on the remaining rows.
 */
export function emitBeancount(
  rows: string[][],
  mapping: ColumnMapping,
  options: ConvertOptions = {},
  hasHeader = true,
): ConvertResult {
  const { results, blockingErrors, context, splitDiagnostics } =
    prepareCsvRowsWithContext(rows, mapping, options, hasHeader);
  const reportSplits = options.rowSplits !== undefined;

  if (blockingErrors.length > 0) {
    return {
      text: "",
      errors: blockingErrors,
      converted: 0,
      excluded: 0,
      dateOrder: context.resolvedOrder,
      outputMode: context.outputMode,
      accounts: [],
      currencies: [],
      openDate: null,
      statementReconciled: false,
      ...(reportSplits ? { splitDiagnostics: [] } : {}),
    };
  }

  let width = Math.max(context.source.length, context.counter.length);
  if (context.categoryIndex !== null) {
    for (const account of context.categoryAccounts.values()) {
      width = Math.max(width, account.length);
    }
  }
  for (const rule of context.descriptionRules) {
    width = Math.max(width, rule.account.length);
  }
  width += 2;

  // A row whose split cannot apply is a row error (never the unsplit
  // posting), so emission and reconciliation see the same partition.
  const exclude = toCsvExcludeSet(options.excludeRowIndexes);
  const selectedSplitDiagnostics = splitDiagnostics.filter(
    (d) => !exclude.has(d.rowIndex),
  );
  const splitFailures = new Map(
    selectedSplitDiagnostics.map((d) => [d.rowIndex, d]),
  );
  const partitionInput: PrepareCsvRowResult[] =
    splitFailures.size === 0
      ? results
      : results.map((prepared) => {
          if (!prepared.ok) return prepared;
          const failure = splitFailures.get(prepared.row.rowIndex);
          return failure === undefined
            ? prepared
            : {
                ok: false,
                rowIndex: prepared.row.rowIndex,
                line: prepared.row.line,
                reason: csvSplitIssueReason(failure.issues[0]),
              };
        });

  // One partition for emission and reconciliation — never re-filter later.
  const selected = selectRowsForReconciliation(
    partitionInput,
    options.excludeRowIndexes,
  );
  for (const row of selected.rows) {
    if (row.split === undefined) continue;
    for (const allocation of row.split.allocations) {
      width = Math.max(width, allocation.account.length + 2);
    }
  }
  const errors: RowError[] = selected.rowErrors.map((error) => ({
    line: error.line,
    reason: error.reason,
  }));
  const entries: string[] = [];
  const usedCounters = new Set<string>();
  const usedCurrencies = new Set<string>();
  let earliest: string | null = null;

  for (const row of selected.rows) {
    const header = row.description
      ? `${row.date} ${context.flag} ${quote(row.description)}`
      : `${row.date} ${context.flag}`;
    const postings = projectCsvRowPostings(row, context.source);
    entries.push(
      [
        header,
        ...postings.map(
          (p) => `  ${p.account.padEnd(width)}${p.amountText} ${p.currency}`,
        ),
      ].join("\n"),
    );
    for (const posting of postings.slice(1)) usedCounters.add(posting.account);
    usedCurrencies.add(row.currency);
    if (earliest === null || row.date < earliest) {
      earliest = row.date;
    }
  }

  const statementOption = options.statement;
  let statement: StatementReconciliationResult | undefined;
  if (statementOption !== undefined) {
    statement = reconcileStatementFromSelection({
      enabled: statementOption.enabled,
      startDateText: statementOption.startDateText,
      endDateText: statementOption.endDateText,
      openingText: statementOption.openingText,
      closingText: statementOption.closingText,
      currency: (statementOption.currency ?? context.fallbackCurrency).trim(),
      sourceAccount: context.source,
      dateOrder: context.order,
      rows: selected.rows,
      rowErrors: selected.rowErrors,
    });
  }

  const emission =
    statement !== undefined
      ? planMatchedStatementEmission({
          result: statement,
          sourceAccount: context.source,
          outputMode: context.outputMode,
          accountWidth: width,
        })
      : null;
  const statementReconciled = emission !== null;

  const accountSet =
    entries.length > 0
      ? new Set([context.source, ...usedCounters])
      : new Set<string>();
  if (emission?.needsEquityOpen) {
    accountSet.add(OPENING_BALANCES_ACCOUNT);
  }
  const accounts = [...accountSet].sort();
  const currencies = [...usedCurrencies].sort();
  const body = entries.length > 0 ? `${entries.join("\n\n")}\n` : "";

  const openDate =
    emission?.openingDate ?? (entries.length > 0 ? earliest : null);

  const sections: string[] = [];
  if (
    context.outputMode === "complete" &&
    entries.length > 0 &&
    openDate !== null
  ) {
    sections.push(
      accounts.map((account) => `${openDate} open ${account}`).join("\n"),
    );
  }
  if (emission?.openingTransaction) {
    sections.push(emission.openingTransaction);
  }
  if (body) {
    sections.push(body.replace(/\n$/, ""));
  }
  if (emission) {
    sections.push(emission.closingAssertion);
  }

  const text =
    sections.length > 0 ? `${sections.join("\n\n")}\n` : "";

  return {
    text,
    errors,
    converted: entries.length,
    excluded: selected.excluded,
    dateOrder: context.resolvedOrder,
    outputMode: context.outputMode,
    accounts,
    currencies,
    openDate: entries.length > 0 || emission?.openingDate ? openDate : null,
    ...(statement !== undefined ? { statement } : {}),
    statementReconciled,
    ...(reportSplits ? { splitDiagnostics: selectedSplitDiagnostics } : {}),
  };
}
